import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';
import api from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verificar se o usuário está autenticado ao carregar a aplicação
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const isAuth = authService.isAuthenticated();

      if (isAuth && token) {
        // Recarregar dados do usuário do backend para garantir permissões atualizadas
        try {
          const response = await authService.verificarToken();
          if (response.usuario) {
            setCurrentUser(response.usuario);
            localStorage.setItem('user', JSON.stringify(response.usuario));
            setIsLoggedIn(true);
          } else {
            authService.logout();
            setCurrentUser(null);
            setIsLoggedIn(false);
          }
        } catch (error) {
          authService.logout();
          setCurrentUser(null);
          setIsLoggedIn(false);
        }
      } else {
        setCurrentUser(null);
        setIsLoggedIn(false);
      }
      
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, senha) => {
    try {
      const data = await authService.login(email, senha);
      
      // Se o usuário tem apenas uma organização, selecionar automaticamente
      if (
        data.usuario.organizationIds &&
        data.usuario.organizationIds.length === 1
      ) {
        // Buscar dados completos da organização
        try {
          const orgResponse = await api.get('/user-organizations');
          
          if (orgResponse.data.organizacoes && orgResponse.data.organizacoes.length > 0) {
            data.usuario.currentOrganization = orgResponse.data.organizacoes[0];
          }
        } catch (orgError) {
          // Silently fail
        }
      }

      // Salvar usuário com permissões atualizadas
      localStorage.setItem('user', JSON.stringify(data.usuario));
      setCurrentUser(data.usuario);
      setIsLoggedIn(true);
      
      return data;
    } catch (error) {
      throw error;
    }
  };

  const selectOrganization = organizacao => {
    if (currentUser) {
      const updatedUser = {
        ...currentUser,
        currentOrganization: organizacao,
      };
      setCurrentUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const logout = () => {
    authService.logout();
    setCurrentUser(null);
    setIsLoggedIn(false);
  };

  const updateUserPermissions = newPermissions => {
    if (currentUser) {
      const updatedUser = { ...currentUser, permissoes: newPermissions };
      setCurrentUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const refreshUserData = async () => {
    try {
      const response = await authService.verificarToken();
      if (response.usuario) {
        setCurrentUser(response.usuario);
        localStorage.setItem('user', JSON.stringify(response.usuario));
      }
    } catch (error) {
      // Silently fail
    }
  };

  const hasPermission = permission => {
    if (!currentUser || !currentUser.permissoes) {
      return false;
    }

    if (currentUser.cargo === 'super_admin') {
      return true;
    }

    return currentUser.permissoes[permission] === true;
  };

  const hasAccessToQuestoesTexto = () => {
    if (!currentUser) return false;

    // Super admin sempre tem acesso
    if (currentUser.cargo === 'super_admin') {
      return true;
    }

    // Verificar permissão específica para questões fechadas
    return currentUser.permissoes?.gerarQuestoesFechadas === true;
  };

  const isRestrictedUser = () => {
    if (!currentUser) return false;

    // Super admin nunca é restrito
    if (currentUser.cargo === 'super_admin') {
      return false;
    }

    // Contar quantas permissões de geração o usuário tem
    const permissoesGeracao = [
      'gerarTextoTeorico',
      'gerarRoteiroVideo',
      'gerarQuestoesFechadas',
      'gerarQuestoesAbertas',
      'gerarQuestoesInep',
    ];

    const permissoesAtivas = permissoesGeracao.filter(
      perm => currentUser.permissoes?.[perm] === true
    );

    // Se tem apenas uma permissão de geração, é usuário restrito
    return permissoesAtivas.length === 1;
  };

  const updateUser = (userData) => {
    const updatedUser = { ...currentUser, ...userData };
    setCurrentUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const value = {
    currentUser,
    user: currentUser, // Alias para compatibilidade
    isLoggedIn,
    loading,
    login,
    logout,
    hasPermission,
    hasAccessToQuestoesTexto,
    isRestrictedUser,
    updateUserPermissions,
    updateUser,
    refreshUserData,
    selectOrganization,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
